'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast, Toaster } from 'sonner';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import PublicFlowHeader from '@/components/PublicFlowHeader';
import { buildRepurchasePaymentUrl, resolvePostLoginRedirect } from '@/lib/authRouting';
import { CREDIT_PACK_OPTIONS, CHECKOUT_PACK_PLAN, formatPackPriceTtc } from '@/lib/creditPacks';
import { publicApiPath } from '@/lib/apiBase';
import { assignWithSessionToken, getAuthToken, pathWithSessionTokenInUrl, waitForAuthSession } from '@/lib/pwaSession';
import MagicMovieOnboardingPopin from '@/components/MagicMovieOnboardingPopin';
import {
  consumePendingSignupPopinAfterIntro,
  hasSeenAuthPopin,
  isRepurchasePopinDismissedThisSession,
  markAuthPopinSeen,
  markRepurchasePopinDismissedThisSession,
} from '@/lib/magicMoviePopin';
import { trackLandingAuthIfApplicable } from '@/lib/analyticsTrack';
import { isStandalonePwa } from '@/lib/pwaDetect';

const INSTI_URL = 'https://magic-movie.dreamerlife.co';

type SubscriptionState = 'active_unconsumed' | 'active_consumed' | 'no_subscription';

type Feature = string | { type: 'rendus'; text: string; options: string[] };

interface PlanDef {
  id: string;
  tag: string;
  name: string;
  pricePerVideo: number;
  priceLaunch: number;
  popular: boolean;
  features: Feature[];
  cta: string;
}

// Correspondance des fichiers vidéos avec le sous-dossier /assets/videos/
const VIDEO_FILES: Record<string, string> = {
  'classique': '/assets/videos/classique.mp4',
  'cinéma': '/assets/videos/cinema.mp4',
  'émotion': '/assets/videos/emotion.mp4',
  'fun': '/assets/videos/fun.mp4',
  'Vidéo exemple': '/assets/videos/selfie.mp4', // Mise à jour ici pour le lien global "Voir un exemple"
};

const UNIFIED_VIDEO_OFFER: PlanDef = {
  id: CHECKOUT_PACK_PLAN,
  tag: 'FILMS SOUVENIRS',
  name: 'Vidéos premium',
  pricePerVideo: 0,
  priceLaunch: 0,
  popular: true,
  features: [
    "Mini films de 30 secondes, rendu professionnel",
    "6 ou 8 scènes guidées selon votre choix au tournage (avec ou sans 2 vidéos d'introduction Prestige)",
    {
      type: 'rendus',
      text: '4 rendus possibles',
      options: ['classique', 'cinéma', 'émotion', 'fun'],
    },
    "Montage rapide, prénoms / date / lieu sur la vidéo",
    "Option copyright DreamerLife ou sans logo selon le format choisi",
    "Crédits valables sur tous vos tournages — choisissez Émotion ou Prestige à chaque film",
    "Packs : 5 vidéos = 29 € TTC · 10 vidéos = 49 € TTC · 15 vidéos = 69 € TTC",
    "Application mobile : tournez à votre rythme",
  ],
  cta: 'Acheter un pack',
};

function AuthFormContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isRepurchase = searchParams.get('repurchase') === '1';

  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [popinVideo, setPopinVideo] = useState<string | null>(null);
  const [showOnboardingPopin, setShowOnboardingPopin] = useState(false);

  const [selectedPlan] = useState<string>(CHECKOUT_PACK_PLAN);
  const [selectedPackBilling, setSelectedPackBilling] = useState<'pack_5' | 'pack_10' | 'pack_15'>('pack_5');

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: { email: '', password: '' }
  });

  useEffect(() => {
    if (searchParams.get('mode') === 'login') {
      setMode('login');
    }
  }, [searchParams]);

  useEffect(() => {
    if (isRepurchase) {
      void (async () => {
        await waitForAuthSession();
        if (!getAuthToken()) return;
        if (!isRepurchasePopinDismissedThisSession()) {
          setShowOnboardingPopin(true);
        }
      })();
      return;
    }
    if (consumePendingSignupPopinAfterIntro()) {
      setShowOnboardingPopin(true);
      return;
    }
    if (!hasSeenAuthPopin()) {
      setShowOnboardingPopin(true);
    }
  }, [isRepurchase]);

  const dismissOnboardingPopin = () => {
    if (isRepurchase) {
      markRepurchasePopinDismissedThisSession();
    } else {
      markAuthPopinSeen();
    }
    setShowOnboardingPopin(false);
  };

  const authPopinPrimaryLabel = isRepurchase
    ? 'Voir les packs vidéo'
    : mode === 'login'
      ? 'Continuer vers la connexion'
      : 'Créer mon Magic Movie';

  useEffect(() => {
    if (!isRepurchase) return;
    if (!getAuthToken()) {
      const returnTo = '/sign-up-login-screen?repurchase=1';
      router.replace(
        `/sign-up-login-screen?mode=login&return=${encodeURIComponent(returnTo)}`,
      );
    }
  }, [isRepurchase, router]);

  useEffect(() => {
    if (isRepurchase) return;

    void (async () => {
      const token = await waitForAuthSession();
      if (!token) return;

      const target = await resolvePostLoginRedirect(token, undefined);
      const dest = target.includes('/sign-up-login-screen')
        ? '/shooting-guide?new=true'
        : target;

      router.replace(
        isStandalonePwa() ? dest : pathWithSessionTokenInUrl(dest, token),
      );
    })();
  }, [router, isRepurchase]);

  const handleRepurchaseContinue = () => {
    setIsLoading(true);
    localStorage.setItem('selectedPlan', selectedPlan);
    localStorage.setItem('checkoutBillingMode', selectedPackBilling);
    localStorage.setItem('checkoutAccountPending', 'false');
    toast.success('Redirection vers le paiement sécurisé…');
    setTimeout(() => {
      assignWithSessionToken(buildRepurchasePaymentUrl(selectedPlan, selectedPackBilling));
      setIsLoading(false);
    }, 400);
  };

  const onSubmit = async (data: { email: string; password: string }) => {
    setIsLoading(true);
    try {
      if (mode === 'signup') {
        const response = await fetch(publicApiPath('/v1/api/register'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: data.email, password: data.password }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
          toast.error(result.data?.message || result.errors || 'Impossible de créer le compte');
          setIsLoading(false);
          return;
        }
        const token = result.data?.token as string | undefined;
        if (!token) {
          toast.error('Erreur lors de la création du compte');
          setIsLoading(false);
          return;
        }
        localStorage.setItem('token', token);
        localStorage.setItem('selectedPlan', 'decouverte');
        trackLandingAuthIfApplicable();
        toast.success('Compte créé — votre 1ère vidéo Découverte est offerte !');
        setTimeout(() => {
          router.push(pathWithSessionTokenInUrl('/shooting-guide?new=true', token));
        }, 600);
      } else {
        const response = await fetch(publicApiPath('/v1/api/login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: data.email, password: data.password }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || result.success === false || result.error) {
          toast.error(result.message || result.data?.message || result.error || "Email ou mot de passe incorrect");
          setIsLoading(false);
          return;
        }

        if (!result.data || !result.data.token) {
          toast.error("Erreur lors de la récupération des accès.");
          setIsLoading(false);
          return;
        }

        localStorage.setItem('token', result.data.token);

        trackLandingAuthIfApplicable();

        const state: SubscriptionState = result.data.subscriptionState;
        const returnPath = searchParams.get('return');
        toast.success('Connexion réussie !');
        
        setTimeout(async () => {
          const authToken = result.data.token as string;
          if (returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')) {
            router.push(pathWithSessionTokenInUrl(returnPath, authToken));
            return;
          }
          const target = await resolvePostLoginRedirect(authToken, state);
          router.push(pathWithSessionTokenInUrl(target, authToken));
        }, 800);
      }
    } catch (err) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="mobile-viewport-shell w-full flex flex-col"
      style={{
        backgroundColor: '#FAF8F6',
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231F1D40' fill-opacity='0.04'%3E%3Cpath d='M0 0h40v1H0zM0 0v40h1V0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
      }}
    >
      <PublicFlowHeader />
      <Toaster position="bottom-right" richColors />

      <div className="w-full max-w-md flex flex-col flex-grow mx-auto px-4 py-8 overflow-y-auto relative">
        <div className="bg-white rounded-[32px] p-6 md:p-8 border border-[#E8E4DF] shadow-xl">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[#1F1D40]" style={{ fontFamily: "'Abel', sans-serif" }}>
              {isRepurchase || mode === 'signup' ? "Commencez l'aventure" : 'Bon retour'}
            </h2>
            {(isRepurchase || mode === 'signup') && (
              <p
                className="mt-3 text-sm leading-relaxed"
                style={{ color: '#8A8598', fontFamily: "'Work Sans', sans-serif" }}
              >
                Créez une vidéo de qualité au rendu professionnel grâce à nos guides et conseils. Nous vous accompagnons à chaque étape pour capturer les meilleures images, tout en vous laissant la liberté de créer un contenu qui vous ressemble.
              </p>
            )}
          </div>

          <form
            onSubmit={isRepurchase ? (e) => { e.preventDefault(); handleRepurchaseContinue(); } : handleSubmit(onSubmit)}
            className="space-y-5"
          >
            
            {isRepurchase && (
              <div className="space-y-4 pb-2">
                <label
                  className="block text-xs font-bold uppercase tracking-wider"
                  style={{ color: '#8A8598', fontFamily: "'Acme', 'Work Sans', sans-serif" }}
                >
                  Vos crédits vidéo
                </label>

                <div className="rounded-2xl border border-[#D41F4C] bg-[#F9F7F5] px-4 sm:px-5 py-4">
                  <h4 className="text-sm font-bold text-[#1F1D40]">{UNIFIED_VIDEO_OFFER.name}</h4>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8598] mt-0.5">{UNIFIED_VIDEO_OFFER.tag}</p>
                  <div className="mt-3 pt-3 border-t border-[#E8E4DF] space-y-2">
                    {UNIFIED_VIDEO_OFFER.features.map((feature, i) => {
                      if (typeof feature === 'string') {
                        return (
                          <div key={i} className="flex items-start gap-2 text-[10px] text-[#4A4560]">
                            <span className="mt-0.5 text-[#D41F4C]">✦</span>
                            <span>{feature}</span>
                          </div>
                        );
                      }
                      if (feature.type === 'rendus') {
                        return (
                          <div key={i} className="flex flex-col gap-1 text-[10px] text-[#4A4560]">
                            <div className="flex items-start gap-2">
                              <span className="mt-0.5 text-[#D41F4C]">✦</span>
                              <span>{feature.text} :</span>
                            </div>
                            <div className="pl-5 flex flex-wrap gap-2">
                              {feature.options.map((opt) => (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPopinVideo(opt); }}
                                  className="text-[#D41F4C] no-underline hover:text-[#b0173d] font-semibold"
                                >
                                  {opt}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                    <div className="flex gap-4 mt-2">
                      <a
                        href={`${INSTI_URL}/#pricing`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] font-bold text-[#D41F4C] no-underline hover:text-[#b0173d]"
                      >
                        Détails de l&apos;offre
                      </a>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setPopinVideo('Vidéo exemple'); }}
                        className="text-[10px] font-bold text-[#D41F4C] no-underline hover:text-[#b0173d]"
                      >
                        Voir un exemple
                      </button>
                    </div>
                  </div>
                </div>

                <label
                  className="block text-xs font-bold uppercase tracking-wider pt-2"
                  style={{ color: '#8A8598', fontFamily: "'Acme', 'Work Sans', sans-serif" }}
                >
                  Taille du pack
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {CREDIT_PACK_OPTIONS.map((pack) => (
                    <button
                      key={pack.billingMode}
                      type="button"
                      onClick={() => setSelectedPackBilling(pack.billingMode)}
                      className={`rounded-xl border px-2 py-3 text-center transition-all ${
                        selectedPackBilling === pack.billingMode
                          ? 'border-[#D41F4C] bg-[#F9F7F5]'
                          : 'border-[#E8E4DF] bg-white'
                      }`}
                    >
                      <span className="block text-lg font-bold text-[#D41F4C]">{pack.size}</span>
                      <span className="block text-[10px] text-[#8A8598]">vidéos</span>
                      <span className="block text-sm font-bold text-[#1F1D40] mt-1">{formatPackPriceTtc(pack.size)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {mode === 'signup' && !isRepurchase && (
              <div className="rounded-2xl border border-[#E8E4DF] bg-[#F9F7F5] px-4 py-3 mb-2">
                <p className="text-sm font-bold text-[#1F1D40]">Votre 1ère vidéo est offerte</p>
                <p className="text-xs text-[#8A8598] mt-1">
                  Offre Découverte incluse à la création du compte — 6 scènes guidées, sans paiement.
                </p>
              </div>
            )}

            {!isRepurchase && (
            <div className="space-y-4">
              {mode === 'signup' && (
                <label 
                  className="block text-xs font-bold uppercase tracking-wider -mb-1"
                  style={{ color: '#8A8598', fontFamily: "'Acme', 'Work Sans', sans-serif" }}
                >
                  {isRepurchase ? '' : 'Créez votre accès'}
                </label>
              )}
              
              <div className="space-y-1">
                <div className="relative">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#B0A898]" />
                  <input
                    {...register('email', { 
                      required: "Adresse email requise", 
                      pattern: { value: /^\S+@\S+\.\S+$/, message: "Format d'email invalide" }
                    })}
                    type="email"
                    placeholder="Adresse email"
                    className="w-full pl-10 pr-4 py-3 rounded-xl text-sm border border-[#E8E4DF] focus:outline-none focus:border-[#D41F4C] transition-colors bg-[#FAF8F6]/40"
                  />
                </div>
                {errors.email && <p className="text-[10px] text-[#D41F4C] font-bold pl-1">{errors.email.message as string}</p>}
              </div>

              {(mode === 'signup' || mode === 'login') && (
                <div className="space-y-1">
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#B0A898]" />
                    <input
                      {...register('password', { 
                        required: "Mot de passe requis", 
                        minLength: { value: 8, message: "8 caractères minimum" }
                      })}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mot de passe"
                      className="w-full pl-10 pr-10 py-3 rounded-xl text-sm border border-[#E8E4DF] focus:outline-none focus:border-[#D41F4C] transition-colors bg-[#FAF8F6]/40"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#B0A898]">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {mode === 'signup' && errors.password && (
                    <p className="text-[10px] text-[#D41F4C] font-bold pl-1">{errors.password.message as string}</p>
                  )}
                </div>
              )}

              {mode === 'login' && (
                <div className="space-y-1">
                  <div className="flex items-start justify-between pt-1">
                    <div className="flex-1">
                      {errors.password && <p className="text-[10px] text-[#D41F4C] font-bold pl-1">{errors.password.message as string}</p>}
                    </div>
                    
                    <button 
                      type="button" 
                      onClick={() => router.push('/forgot-password')} 
                      className="text-[10px] text-[#8A8598] font-bold hover:text-[#D41F4C] hover:underline transition-colors shrink-0"
                      style={{ fontFamily: "'Work Sans', sans-serif" }}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-[#D41F4C] text-white rounded-2xl font-bold text-sm shadow-lg hover:opacity-95 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
              style={{ fontFamily: "'Acme', sans-serif" }}
            >
              {isLoading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  {isRepurchase ? 'Payer mon pack' : mode === 'signup' ? 'Créer mon compte gratuit' : 'Se connecter'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>

            {!isRepurchase && (
            <div className="pt-3 text-center">
              <p className="text-sm" style={{ color: '#8A8598', fontFamily: "'Acme', 'Work Sans', sans-serif" }}>
                {mode === 'signup' ? 'Déjà inscrit ?' : 'Pas encore de compte ?'}
                {' '}
                <button 
                  type="button"
                  onClick={() => { setMode(mode === 'signup' ? 'login' : 'signup'); reset(); }} 
                  className="font-bold hover:underline transition-all"
                  style={{ color: '#D41F4C' }}
                >
                  {mode === 'signup' ? 'Se connecter' : 'Créer un compte'}
                </button>
              </p>
            </div>
            )}
          </form>
        </div>

        <p className="text-center text-[10px] mt-5 text-[#8A8598] font-medium" style={{ fontFamily: "'Work Sans', sans-serif" }}>
          © 2026 DreamerLife. Tous droits réservés.
        </p>
      </div>

      {/* Pop-in de lecture vidéo responsive */}
      {popinVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-6" 
          onClick={() => setPopinVideo(null)}
        >
          <div 
            className="bg-white rounded-3xl p-4 md:p-6 w-full max-w-2xl lg:max-w-3xl shadow-2xl border border-[#E8E4DF] flex flex-col relative animate-in fade-in zoom-in-95 duration-200" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-[#1F1D40] capitalize text-base md:text-lg" style={{ fontFamily: "'Abel', sans-serif" }}>
                Rendu : {popinVideo}
              </h3>
              <button 
                onClick={() => setPopinVideo(null)} 
                className="text-[#8A8598] hover:text-[#D41F4C] font-bold text-2xl transition-colors p-1 leading-none"
              >
                &times;
              </button>
            </div>
            
            {/* Conteneur vidéo avec ratio 16:9 fluide */}
            <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden flex items-center justify-center shadow-inner relative">
              <video 
                key={popinVideo} 
                src={VIDEO_FILES[popinVideo] || '/assets/videos/selfie.mp4'} 
                controls 
                playsInline
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {showOnboardingPopin && (
        <MagicMovieOnboardingPopin
          open
          onClose={dismissOnboardingPopin}
          onPrimary={dismissOnboardingPopin}
          primaryLabel={authPopinPrimaryLabel}
        />
      )}
    </div>
  );
}

export default function AuthForm() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#FAF8F6]" />}>
      <AuthFormContent />
    </Suspense>
  );
}